import { PackageDataSpec } from '@cadstrom/cadstrom-shared';
import StarIcon from '@mui/icons-material/Star';
import Chip from '@mui/material/Chip';
import clsx from 'clsx';

import { CLAUSE_CHIP_CLASS_NAME } from './clause-chip-class-name.js';
import { clauseCode, qualifiedClauseCode } from './clause-label.js';
import { ClauseTooltip } from './clause-tooltip.js';

export interface ClauseChipProps {
	clause: PackageDataSpec.PackageDataSpecClause;
	/** Shows the chip as the primary citation: filled, with a star. */
	primary?: boolean;
	/** Shows the property before the clause number (`pinout #12`), for lists that mix properties. */
	qualified?: boolean;
	/** Shows the short name after the clause number. Turn off for a shorter chip; the tooltip still shows it. */
	showShortName?: boolean;
	onDelete?: () => void;
	/** Called when the chip is clicked, to make it the primary citation. */
	onMakePrimary?: () => void;
	className?: string;
}

/**
 * A chip showing a clause's number and, by default, its short name. Hovering shows the version, its author,
 * the short name, and the full clause text. A retired clause is greyed out and struck through, since old
 * verdicts may still cite it.
 *
 * @param props - {@link ClauseChipProps}.
 * @returns The chip element.
 */
export function ClauseChip({
	clause,
	primary = false,
	qualified = false,
	showShortName = true,
	onDelete,
	onMakePrimary,
	className,
}: ClauseChipProps) {
	const retired = clause.status === PackageDataSpec.PackageDataSpecStatus.Retired;
	const code = qualified ? qualifiedClauseCode(clause) : clauseCode(clause);
	return (
		<ClauseTooltip
			clause={clause}
			qualified={qualified}
			hint={onMakePrimary === undefined ? undefined : 'Click to make this the primary clause.'}
		>
			<Chip
				size='small'
				label={showShortName ? `${code} ${clause.shortName}` : code}
				icon={primary ? <StarIcon className='text-sm!' /> : undefined}
				variant={primary ? 'filled' : 'outlined'}
				color={
					retired ? 'gray'
					: primary ?
						'primary'
					:	'gray'
				}
				onClick={onMakePrimary}
				onDelete={onDelete}
				className={clsx(
					CLAUSE_CHIP_CLASS_NAME,
					// MUI's own icon margin sits the star inside the pill's corner radius.
					'[&_.MuiChip-icon]:ml-2',
					retired && 'line-through opacity-70',
					className,
				)}
			/>
		</ClauseTooltip>
	);
}
